/* RuleLogicView — the rule's EXECUTED logic (the structured `logic` object), shown so an SME can read
   it: WHEN / THEN (or ALWAYS) sections, one row per condition, values as chips, collapsible ALL / ANY /
   NOT groups, and a Text view with copy. Falls back to the given text pseudocode when a rule has no
   structured logic. Self-contained: styles are injected once. */
import React, { useState } from "react";

type Cond = any;

const OP_WORD: Record<string, string> = {
  in: "is one of", not_in: "is none of", contains_any: "contains any of", contains_all: "contains all of",
  contains_none: "contains none of", not_null: "is filled", is_null: "is blank", eq: "=", ne: "≠",
  lt: "<", le: "≤", gt: ">", ge: "≥", multiple_of: "is a multiple of", matches: "matches",
  exists: "has a matching row in", not_exists: "has NO matching row in",
};
const NEGATIVE = new Set(["not_in", "contains_none", "is_null", "ne", "not_exists"]);

export function getLogic(rule: any): any | null {
  if (!rule) return null;
  const cands = [rule.logic, rule._raw?.logic, rule.content?.logic, rule.current_content?.logic];
  for (const c of cands) if (c && typeof c === "object" && Object.keys(c).length) return c;
  return null;
}

function splitField(f: string): [string, string] {
  const s = String(f || "");
  const i = s.indexOf("::");
  return i > 0 ? [s.slice(0, i), s.slice(i + 2)] : ["", s];
}

function condText(c: Cond): string {
  if (!c || typeof c !== "object") return "?";
  if (c.all) return "(" + c.all.map(condText).join(" AND ") + ")";
  if (c.any) return "(" + c.any.map(condText).join(" OR ") + ")";
  if (c.not) return "NOT " + condText(c.not);
  const op = String(c.op || "");
  if (op === "not_null" || op === "is_null") return `${c.field} ${OP_WORD[op]}`;
  if (op === "exists" || op === "not_exists")
    return `${c.field} ${OP_WORD[op]} ${c.table} (${(c.match || []).map((m: any) => `${m[0]} = ${m[1]}`).join(" AND ")})`;
  if (op === "matches") return `${c.field} matches ${c.pattern}`;
  const rhs = c.field2 ? c.field2 : Array.isArray(c.values) ? `[${c.values.join(", ")}]` : String(c.value);
  const at = c.field2 && c.match ? ` (matched on ${(c.match || []).map((m: any) => `${m[0]} = ${m[1]}`).join(" AND ")})` : "";
  return `${c.field} ${OP_WORD[op] || op} ${rhs}${at}`;
}

export function logicText(lg: any): string {
  if (!lg) return "";
  if (lg.assert) return "ALWAYS: " + condText(lg.assert);
  return (lg.when ? `WHEN ${condText(lg.when)}\nTHEN ` : "ALWAYS: ") + condText(lg.then);
}

function Chips({ values }: { values: any[] }) {
  const [all, setAll] = useState(false);
  const vs = (values || []).map((v) => String(v));
  const shown = all ? vs : vs.slice(0, 8);
  return (
    <span className="rlv-chips">
      {shown.map((v, i) => <span key={i} className="rlv-chip">{v === "" ? "∅" : v}</span>)}
      {vs.length > 8 && (
        <button className="rlv-more" onClick={() => setAll((x) => !x)}>
          {all ? "show less" : `+${vs.length - 8} more`}
        </button>
      )}
    </span>
  );
}

function FieldTag({ f }: { f: string }) {
  const [t, c] = splitField(f);
  return <span className="rlv-field">{t && <span className="rlv-tab">{t}</span>}<b>{c}</b></span>;
}

function Atom({ c }: { c: Cond }) {
  const op = String(c.op || "");
  const neg = NEGATIVE.has(op);
  return (
    <div className="rlv-atom">
      <FieldTag f={c.field} />
      <span className={"rlv-op" + (neg ? " neg" : "")}>{OP_WORD[op] || op}</span>
      {op === "exists" || op === "not_exists" ? (
        <span className="rlv-exists">
          <span className="rlv-tab">{c.table}</span>
          {(c.match || []).map((m: any, i: number) => (
            <span key={i} className="rlv-match"><FieldTag f={m[0]} /> = <FieldTag f={m[1]} /></span>
          ))}
        </span>
      ) : c.field2 ? (
        <span className="rlv-exists">
          <FieldTag f={c.field2} />
          {(c.match || []).map((m: any, i: number) => (
            <span key={i} className="rlv-match">at <FieldTag f={m[0]} /> = <FieldTag f={m[1]} /></span>
          ))}
        </span>
      )
        : op === "matches" ? <code className="rlv-pat">{c.pattern}</code>
        : Array.isArray(c.values) ? <Chips values={c.values} />
        : c.value !== undefined && c.value !== null ? <span className="rlv-chip">{String(c.value)}</span> : null}
    </div>
  );
}

function Node({ c, depth = 0 }: { c: Cond; depth?: number; key?: any }) {
  if (!c || typeof c !== "object") return null;
  const kind = c.all ? "ALL of" : c.any ? "ANY of" : c.not ? "NOT" : null;
  if (!kind) return <Atom c={c} />;
  const kids: Cond[] = c.all || c.any || [c.not];
  if ((c.all || c.any) && kids.length === 1) return <Node c={kids[0]} depth={depth} />;
  return (
    <details className={"rlv-group" + (c.not ? " not" : "")} open={depth < 2}>
      <summary><span className="rlv-gk">{kind}</span>{!c.not && <span className="rlv-gn">{kids.length} conditions</span>}</summary>
      <div className="rlv-kids">{kids.map((k, i) => <Node key={i} c={k} depth={depth + 1} />)}</div>
    </details>
  );
}

export default function RuleLogicView({ rule, fallback, compact = false }:
  { rule: any; fallback?: string; compact?: boolean }) {
  ensureStyles();
  const lg = getLogic(rule);
  const [mode, setMode] = useState<"struct" | "text">("struct");
  const [copied, setCopied] = useState(false);
  if (!lg) {
    return (
      <div className="rlv-card">
        {!compact && <div className="rlv-head"><span className="rlv-note">text pseudocode (this rule has no structured logic)</span></div>}
        <pre className="dqc-pseudo">{fallback || "—"}</pre>
      </div>
    );
  }
  const txt = logicText(lg);
  const copy = () => { try { navigator.clipboard.writeText(txt); setCopied(true); setTimeout(() => setCopied(false), 1200); } catch { /* ignore */ } };
  return (
    <div className={"rlv-card" + (compact ? " compact" : "")}>
      <div className="rlv-head">
        <span className="rlv-seg">
          <button className={mode === "struct" ? "on" : ""} onClick={() => setMode("struct")}>Structured</button>
          <button className={mode === "text" ? "on" : ""} onClick={() => setMode("text")}>Text</button>
        </span>
        <span className="rlv-note">this is exactly what executes</span>
        <button className="rlv-copy" onClick={copy}>{copied ? "copied ✓" : "copy"}</button>
      </div>
      {mode === "text" ? <pre className="rlv-text">{txt}</pre> : (
        <div className="rlv-body">
          {lg.assert ? (
            <section className="rlv-sec always"><div className="rlv-lab">ALWAYS</div><Node c={lg.assert} /></section>
          ) : (
            <>
              {lg.when && <section className="rlv-sec when"><div className="rlv-lab">WHEN</div><Node c={lg.when} /></section>}
              <section className="rlv-sec then"><div className="rlv-lab">{lg.when ? "THEN" : "ALWAYS"}</div><Node c={lg.then} /></section>
            </>
          )}
        </div>
      )}
    </div>
  );
}

let _styled = false;
function ensureStyles() {
  if (_styled || typeof document === "undefined") return;
  _styled = true;
  const el = document.createElement("style");
  el.setAttribute("data-rlv", "1");
  el.textContent = `
.rlv-card{border:1px solid #d9e2ec;border-radius:10px;background:#fff;overflow:hidden}
.rlv-card.compact{border:none}
.rlv-head{display:flex;align-items:center;gap:10px;padding:7px 10px;background:#f4f7fb;border-bottom:1px solid #e3e9f0}
.rlv-note{font-size:11.5px;color:#5f6e7d;flex:1}
.rlv-seg{display:inline-flex;border:1px solid #c9d4e0;border-radius:7px;overflow:hidden}
.rlv-seg button{border:none;background:#fff;padding:3px 10px;font-size:12px;cursor:pointer;color:#33475b}
.rlv-seg button.on{background:#0e2436;color:#fff}
.rlv-copy{border:1px solid #c9d4e0;background:#fff;border-radius:6px;font-size:11.5px;padding:2px 8px;cursor:pointer}
.rlv-body{padding:10px 12px;display:flex;flex-direction:column;gap:8px}
.rlv-sec{border-left:4px solid #8aa0b8;padding:6px 10px;border-radius:6px;background:#fafcfe}
.rlv-sec.when{border-left-color:#3b82c4}
.rlv-sec.then{border-left-color:#0e8a6e;background:#f3faf7}
.rlv-sec.always{border-left-color:#7b5cc4;background:#f7f5fc}
.rlv-lab{font-size:10.5px;font-weight:700;letter-spacing:.08em;color:#5f6e7d;margin-bottom:4px}
.rlv-atom{display:flex;flex-wrap:wrap;align-items:center;gap:6px;padding:3px 0;font-size:13px}
.rlv-field{display:inline-flex;align-items:center;gap:4px;font-family:var(--dqc-mono,monospace)}
.rlv-tab{font-size:10px;font-weight:700;color:#4a5d72;background:#e6edf5;border-radius:4px;padding:1px 5px;font-family:inherit}
.rlv-op{font-size:12px;color:#1d4f7a;background:#e8f1fa;border-radius:10px;padding:1px 8px}
.rlv-op.neg{color:#8a3b12;background:#fbeee6}
.rlv-chips{display:inline-flex;flex-wrap:wrap;gap:4px}
.rlv-chip{font-family:var(--dqc-mono,monospace);font-size:12px;background:#fff;border:1px solid #c9d4e0;border-radius:5px;padding:0 6px}
.rlv-more{border:none;background:none;color:#1d4f7a;font-size:11.5px;cursor:pointer;text-decoration:underline}
.rlv-pat{font-size:12px;background:#f1f1f1;padding:1px 5px;border-radius:4px;word-break:break-all}
.rlv-exists{display:inline-flex;flex-wrap:wrap;align-items:center;gap:6px}
.rlv-match{display:inline-flex;align-items:center;gap:4px;font-size:12px;color:#5f6e7d}
.rlv-group{border:1px dashed #c9d4e0;border-radius:7px;padding:2px 8px;margin:3px 0;background:#fff}
.rlv-group.not{border-color:#e0b9a0;background:#fffaf6}
.rlv-group>summary{cursor:pointer;font-size:12px;color:#33475b;list-style:none;display:flex;gap:8px;align-items:center;padding:2px 0}
.rlv-group>summary::before{content:"▸";font-size:10px;color:#8aa0b8}
.rlv-group[open]>summary::before{content:"▾"}
.rlv-gk{font-weight:700;font-size:11px;letter-spacing:.05em}
.rlv-gn{font-size:11px;color:#8aa0b8}
.rlv-kids{padding-left:12px;border-left:2px solid #e3e9f0;margin:2px 0 4px 4px}
.rlv-text{margin:0;padding:10px 12px;background:#0e2436;color:#d7e5f5;font-family:var(--dqc-mono,monospace);font-size:12.5px;white-space:pre-wrap;word-break:break-word}
.rlv-flags{display:inline-flex;gap:4px;margin-left:6px;vertical-align:middle}
.rlv-flag{font-size:10.5px;font-weight:600;border-radius:9px;padding:1px 7px;background:#fdecea;color:#9b2c1f;border:1px solid #f3c2bb;cursor:help}
.rlv-flag.cannot_fail,.rlv-flag.not_tested{background:#f1f3f6;color:#4a5d72;border-color:#d6dde6}
.rlv-flagnotes{margin:6px 0 10px;padding:8px 10px;border-radius:8px;background:#fff6f4;border:1px solid #f3d3cc;font-size:12.5px;color:#7a2e22}
`;
  document.head.appendChild(el);
}

export const HIDDEN_FLAGS = new Set(["cannot_fail", "contradicted", "not_tested", "rejected_by_data", "equivalent_to", "merged_into", "implied_by", "insufficient_sample", "integrity_check"]);
export const FLAG_LABEL: Record<string, string> = { reference_gap: "rule correct · reference gap", clean_control: "clean today · valid control",
  cannot_fail: "can't fail", contradicted: "contradicted by data", not_tested: "not tested",
  rejected_by_data: "rejected by data", equivalent_to: "duplicate (same result)",
  mergeable_with: "merge with similar rules", merged_into: "merged into a consolidated rule",
  implied_by: "covered by a broader rule",
  insufficient_sample: "too few rows to judge", integrity_check: "integrity check (not a business rule)",
  text_out_of_sync: "description differs from the logic",
};
export function flagsOf(r: any): string[] {
  const f = r?.review_flags || r?._raw?.review_flags || [];
  return Array.isArray(f) ? f : [];
}
export function flagNotesOf(r: any): Record<string, string> {
  return r?.review_flag_notes || r?._raw?.review_flag_notes || {};
}
export function FlagBadges({ r }: { r: any }) {
  ensureStyles();
  const fl = flagsOf(r);
  if (!fl.length) return null;
  const notes = flagNotesOf(r);
  return (
    <span className="rlv-flags">
      {fl.map((f) => <span key={f} className={"rlv-flag " + f} title={notes[f] || ""}>⚑ {FLAG_LABEL[f] || f}</span>)}
    </span>
  );
}

/* GRP_v2 - a newer version than the current one is a PROPOSAL awaiting the SME (from consolidation or from
   dq_dedups finding a new version of an approved rule). Shown on the card and in the detail. */
export function pendingProposal(r: any): { version: number; source: string } | null {
  const revs: any[] = r?.revisions || r?._raw?.revisions || [];
  const cur = Number(r?.version ?? r?._raw?.version ?? r?.current_version ?? 1);
  const newer = revs.filter((v) => Number(v?.version) > cur && !v?.superseded_before_review);
  if (!newer.length) return null;
  const top = newer.sort((a, b) => Number(b.version) - Number(a.version))[0];
  const ct = String(top?.change_type || top?.change_sub_type || "");
  return { version: Number(top.version), source: (ct === "consolidated" ? "consolidation" : "dq_dedups")
           + (top?.relation ? `: ${top.relation}` : "") };
}
export function ProposalBadge({ r }: { r: any }) {
  ensureStyles();
  const p = pendingProposal(r);
  if (!p) return null;
  return <span className="rlv-flag" style={{ background: "#fff4d6", color: "#7a5300", borderColor: "#f0d48a" }}
    title={`A proposed change (${p.source}) is waiting for your decision - open the version history to compare and accept`}>
    ✎ proposal v{p.version}</span>;
}
export function ProposalBanner({ r }: { r: any }) {
  ensureStyles();
  const p = pendingProposal(r);
  if (!p) return null;
  return <div className="rlv-flagnotes" style={{ background: "#fff8e6", borderColor: "#f0d48a", color: "#5e4300" }}>
    <b>✎ Proposed change v{p.version} awaiting your decision</b> ({p.source}). The current version stays active until you
    accept it - use <b>History → Rule versions</b> to compare v{p.version} with the current version (each with its own
    result on the data) and make it active, or decline it.
  </div>;
}
